import React, { useEffect, useRef, useState } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { Container, Row, Col, Card, Spinner, Alert } from 'react-bootstrap';
import { apiService } from '../services/api';
import './PaymentCallbackPage.scss';

const PaymentCallbackPage: React.FC = () => {
  const { cartId, gateway } = useParams<{ cartId: string; gateway: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [message, setMessage] = useState<string>('');

  const processedKeyRef = useRef<string | null>(null);
  const formSubmittedRef = useRef<boolean>(false);

  useEffect(() => {
    // Build a stable key from inputs to prevent duplicate execution in React StrictMode
    const paramsKey = searchParams.toString();
    const executionKey = `${cartId || ''}|${gateway || ''}|${paramsKey}`;

    // If we've already processed this exact key, skip
    if (processedKeyRef.current === executionKey) {
      return;
    }

    const handlePaymentCallback = async () => {
      if (!cartId || !gateway) {
        setError('Invalid payment callback parameters');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);

        // Handle different gateways differently
        if (gateway === 'sslc') {
          // SSLCommerz: POST data is already stored in session by Django callback receiver
          // Django receives POST from SSLCommerz, stores it, and redirects here with GET
          // Check status from URL params
          const status = searchParams.get('status');
          
          // If status is cancel or failure, handle accordingly
          if (status === 'cancel') {
            setError('You cancelled the SSLCommerz payment.');
            setLoading(false);
            return;
          }
          
          if (status === 'failure') {
            setError('Your SSLCommerz payment has failed. Please try again or use a different method.');
            setLoading(false);
            return;
          }
          
          // For success, call validation endpoint which retrieves data from session
          const result = await apiService.payment.validateSSLC(parseInt(cartId));
          
          if (result.success) {
            setSuccess(true);
            setMessage(result.message || 'Payment processed successfully');
            
            setTimeout(() => {
              if (result.redirect_url) {
                navigate(result.redirect_url);
              } else {
                navigate(`/checkout/${cartId}/confirmation/`);
              }
            }, 2000);
          } else {
            setError(result.error || 'Payment processing failed');
          }
        } else if (gateway === 'bkash') {
          // bKash: Extract paymentID and status from query params
          const paymentID = searchParams.get('paymentID');
          const status = searchParams.get('status');
          
          if (!paymentID || !status) {
            setError('Missing payment callback parameters');
            setLoading(false);
            return;
          }
          
          const result = await apiService.payment.validateBkash(
            parseInt(cartId),
            paymentID,
            status
          );
          
          if (result.success) {
            setSuccess(true);
            setMessage(result.message || 'Payment processed successfully');
            
            setTimeout(() => {
              if (result.redirect_url) {
                navigate(result.redirect_url);
              } else {
                navigate(`/checkout/${cartId}/confirmation/`);
              }
            }, 2000);
          } else {
            setError(result.error || 'Payment processing failed');
          }
        } else if (gateway === 'pathao-pay') {
          // Pathao Pay: Similar to SSLCommerz - callback data is stored in session by Django callback receiver
          // Django receives callback from Pathao Pay, processes it, stores in session, and redirects here with GET
          // Check status from URL params
          const status = searchParams.get('status');
          const errorParam = searchParams.get('error');
          
          // If status is cancel or failure, handle accordingly
          if (status === 'failure') {
            setError(errorParam || 'Your Pathao Pay payment has failed. Please try again or use a different method.');
            setLoading(false);
            return;
          }
          
          // For success, call validation endpoint which retrieves data from session
          const result = await apiService.payment.validatePathaoPay(parseInt(cartId));
          
          if (result.success) {
            setSuccess(true);
            setMessage(result.message || 'Payment processed successfully');
            
            setTimeout(() => {
              if (result.redirect_url) {
                navigate(result.redirect_url);
              } else {
                navigate(`/checkout/${cartId}/confirmation/`);
              }
            }, 2000);
          } else {
            setError(result.error || 'Payment processing failed');
          }
        } else {
          // Other gateways: Use existing callback handler
          const callbackParams: Record<string, string> = {};
          searchParams.forEach((value, key) => {
            callbackParams[key] = value;
          });

          console.log('Processing payment callback:', {
            cartId,
            gateway,
            params: callbackParams
          });

          const result = await apiService.payment.handlePaymentCallback(
            parseInt(cartId),
            gateway,
            callbackParams
          );

          if (result.success) {
            setSuccess(true);
            setMessage(result.message || 'Payment processed successfully');
            
            setTimeout(() => {
              if (result.redirect_url) {
                navigate(result.redirect_url);
              } else {
                navigate(`/checkout/${cartId}/confirmation/`);
              }
            }, 2000);
          } else {
            setError(result.message || 'Payment processing failed');
          }
        }
      } catch (error: any) {
        console.error('Error processing payment callback:', error);
        setError(error.response?.data?.error || 'Failed to process payment callback. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    // Mark this key as processed before invoking; dedupe at-source
    processedKeyRef.current = executionKey;
    handlePaymentCallback();
  }, [cartId, gateway, searchParams, navigate]);

  if (loading) {
    return (
      <div className="payment-callback-page">
        <Container className="py-5">
          <Row className="justify-content-center">
            <Col xs={12} sm={10} md={8} lg={6}>
              <Card className="text-center border-0 shadow-lg glass">
                <Card.Body className="py-5">
                  <div className="mb-4">
                    <Spinner animation="border" role="status" className="text-primary">
                      <span className="visually-hidden">Processing payment...</span>
                    </Spinner>
                  </div>
                  <h3 className="mb-3">Processing Payment</h3>
                  <p className="text-muted">
                    Please wait while we process your payment...
                  </p>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    );
  }

  if (error) {
    return (
      <div className="payment-callback-page">
        <Container className="py-5">
          <Row className="justify-content-center">
            <Col xs={12} sm={10} md={8} lg={6}>
              <Card className="text-center border-0 shadow-lg glass">
                <Card.Body className="py-5">
                  <div className="mb-4">
                    <i className="fas fa-times-circle text-danger fa-4x"></i>
                  </div>
                  <h3 className="mb-3 text-danger">Payment Failed</h3>
                  <Alert variant="danger" className="text-start">
                    {error}
                  </Alert>
                  <div className="d-grid gap-2">
                    <button 
                      className="btn btn-primary"
                      onClick={() => navigate('/cart')}
                    >
                      <i className="fas fa-shopping-cart me-2"></i>
                      Back to Cart
                    </button>
                    <button 
                      className="btn btn-outline-secondary"
                      onClick={() => navigate('/events')}
                    >
                      <i className="fas fa-search me-2"></i>
                      Browse Events
                    </button>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    );
  }

  if (success) {
    return (
      <div className="payment-callback-page">
        <Container className="py-5">
          <Row className="justify-content-center">
            <Col xs={12} sm={10} md={8} lg={6}>
              <Card className="text-center border-0 shadow-lg glass">
                <Card.Body className="py-5">
                  <div className="mb-4">
                    <i className="fas fa-check-circle text-success fa-4x"></i>
                  </div>
                  <h3 className="mb-3 text-success">Payment Successful!</h3>
                  <Alert variant="success" className="text-start">
                    {message}
                  </Alert>
                  <p className="text-muted">
                    Redirecting to confirmation page...
                  </p>
                  <Spinner size="sm" className="text-primary" />
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    );
  }

  return null;
};

export default PaymentCallbackPage;
